import { Dialog as DialogPrimitive, VisuallyHidden } from 'radix-ui'
import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef } from 'react'
import { cn } from '#lib/utils'

export const DialogRoot = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogPortal = DialogPrimitive.Portal
export const DialogClose = DialogPrimitive.Close
export const DialogTitle = DialogPrimitive.Title
export const DialogDescription = DialogPrimitive.Description
export const VisuallyHiddenRoot = VisuallyHidden.Root

// 遮罩只压暗、不模糊：背后的内容保持可读，能看清弹窗要操作的是什么。
export const DIALOG_OVERLAY_CLASS = 'inset-0 z-0 pointer-events-auto bg-overlay'
export const DIALOG_SURFACE_CLASS =
  'z-10 pointer-events-auto rounded-xl border border-border bg-card text-card-foreground shadow-dialog focus:outline-none'

export type DialogHeaderVariant = 'bordered' | 'plain' | 'alert'
export type DialogTone = 'neutral' | 'ai' | 'destructive'

/** 弹窗默认落在 480px 短表单档；其余档位由调用方按内容覆盖宽度。 */
export const DIALOG_LAYOUT_CLASS = 'flex w-[480px] max-w-[92vw] max-h-[88vh] flex-col'

export const DIALOG_OPEN_MOTION_CLASS =
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-97 data-[state=open]:duration-[160ms] data-[state=open]:ease-[cubic-bezier(0.16,1,0.3,1)]'
export const DIALOG_CLOSE_MOTION_CLASS =
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-97 data-[state=closed]:duration-[120ms] data-[state=closed]:ease-[cubic-bezier(0.16,1,0.3,1)]'
export const DIALOG_OVERLAY_OPEN_MOTION_CLASS =
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-[160ms] data-[state=open]:ease-[cubic-bezier(0.16,1,0.3,1)]'
// 不受 Radix 管理的遮罩（tab 内弹窗）淡出后停在透明，不会在弹窗层移除前闪回
export const DIALOG_OVERLAY_CLOSE_MOTION_CLASS =
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:fill-mode-forwards data-[state=closed]:duration-[120ms] data-[state=closed]:ease-[cubic-bezier(0.16,1,0.3,1)]'
/**
 * Radix 的 Portal 按弹窗层自身有没有动画决定关闭时立刻移除还是等动画结束；弹窗层关闭时播一段
 * 与弹窗退场等长、看不出变化的动画，里面的遮罩和弹窗才来得及播完各自的退场。
 */
export const DIALOG_LAYER_CLOSE_HOLD_CLASS =
  'data-[state=closed]:animate-out data-[state=closed]:duration-[120ms]'

const DIALOG_HEADER_BASE_CLASS = 'flex shrink-0 items-center justify-between gap-3'

/** 带说明的标题栏更高一档；说明由 `data-dialog-description` 标记，两种应用共用这一条规则。 */
export function dialogHeaderClass(variant: DialogHeaderVariant = 'bordered'): string {
  if (variant === 'alert') return 'flex shrink-0 items-start gap-4 px-6 pt-6'
  if (variant === 'plain') {
    return 'flex shrink-0 items-start justify-between gap-3 pb-2 pl-6 pr-4 pt-5'
  }
  return `${DIALOG_HEADER_BASE_CLASS} min-h-14 border-b border-border py-3 pl-6 pr-4 has-[[data-dialog-description]]:min-h-[74px]`
}

export const DIALOG_TITLE_CLASS = 'block text-base font-semibold leading-6 text-card-foreground'

export const DIALOG_DESCRIPTION_CLASS = 'mt-0.5 text-[13px] leading-5 text-muted-foreground'

export const DIALOG_CLOSE_BUTTON_CLASS =
  "inline-flex size-8 shrink-0 items-center justify-center rounded-md p-0 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"

export const DIALOG_ALERT_ICON_CLASS =
  'inline-flex size-9 shrink-0 items-center justify-center rounded-lg'

export const DIALOG_ALERT_TONE_CLASS: Record<DialogTone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  ai: 'bg-ai-muted text-ai-muted-foreground',
  destructive: 'bg-destructive/10 text-destructive'
}

export const DIALOG_ALERT_DESCRIPTION_CLASS = 'mt-1.5 text-sm leading-[22px] text-muted-foreground'

export const DialogOverlay = forwardRef<
  ComponentRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn('fixed', DIALOG_OVERLAY_CLASS, className)}
      {...props}
    />
  )
})

export const DialogSurface = forwardRef<
  ComponentRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(function DialogSurface({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Content ref={ref} className={cn(DIALOG_SURFACE_CLASS, className)} {...props} />
  )
})
